import Popover, { positionBounded } from '@components/Popover/Popover';
import useNode from '@hooks/use-node';
import { useEffect, useState } from 'react';
import type { ColorResult } from 'react-color';
import { ChromePicker } from 'react-color';

import { COLORS, CustomColor } from './ColorSelector';
import * as styles from './ThemeSwatchRow.css';

interface ThemeSwatchRowProps {
  color: string | undefined;
  onSelect: (color: string) => void;
  disabled?: boolean;
}

const PRESET_HEXES = Object.values(COLORS);

function isCustomColor(color: string | undefined) {
  if (!color || PRESET_HEXES.includes(color)) return false;
  return true;
}

const ThemeSwatchRow: React.FC<ThemeSwatchRowProps> = ({ color, onSelect, disabled = false }) => {
  const [customColorNode, customColorRef] = useNode<HTMLElement>();
  const [showCustomColorPicker, setShowCustomColorPicker] = useState(false);
  const [customColor, setCustomColor] = useState(color);

  useEffect(() => {
    if (color) setCustomColor(color);
  }, [color]);

  function handleCustomColorSelect(result: ColorResult) {
    onSelect(result.hex.toUpperCase());
  }

  return (
    <div>
      <ul className={styles.swatchRow}>
        <li ref={customColorRef}>
          <CustomColor
            selectedHex={color}
            onClick={() => setShowCustomColorPicker(true)}
            selected={isCustomColor(color)}
            disabled={disabled}
          />
        </li>
        {PRESET_HEXES.map((hex) => {
          const selected = color === hex;
          return (
            <li key={hex}>
              <button
                type="button"
                className={styles.swatch}
                style={{ '--swatch-color': hex } as React.CSSProperties}
                onClick={() => onSelect(hex)}
                data-selected={selected}
                disabled={disabled}
                aria-label={`Select color ${hex}`}
                aria-pressed={selected}
              />
            </li>
          );
        })}
      </ul>
      <Popover
        positioner={positionBounded({ offsetY: 8 })}
        targetNode={customColorNode}
        show={showCustomColorPicker}
        onClose={() => setShowCustomColorPicker(false)}
        preventOutsideInteraction
      >
        <ChromePicker
          color={customColor}
          onChange={(result) => setCustomColor(result.hex)}
          onChangeComplete={handleCustomColorSelect}
          disableAlpha
        />
      </Popover>
    </div>
  );
};

export default ThemeSwatchRow;
